"use client";
import { useState, useEffect, Fragment } from "react";
import {
  NextButton,
  PrevButton,
} from "../../../../components/new_components/buttonsFlow";
import AnimationComponent from "../../../../components/new_components/animation";
import VisaLogo from "./cardComponents/logoVisa";
import MastercardLogo from "./cardComponents/logoMasterCard";
import StepInput from "../../../../components/new_components/StepInput";
import AnimatedCard from "./cardComponents/animationsCard";
import { ReactComponent as GalileIcon } from "../../../../public/logos/galilea-icono.svg";
import { useWindowSize } from "../../helpers/bankDimensions";
import { stepsCreditStore } from "../../../../store/creditStore/stepsCreditStore";
import { useCreditStore } from "../../../../store/creditStore/creditUserStore";
import { variablesGlobales } from "../../../../configs/variables";
import axios from "axios";
import { useAlert } from "./provider/alertProvider";
import { normalizeMessage } from "./cardComponents/normalizeMessage";
import {
  detectCardNetwork,
  formatCardNumber,
  formatExpiry,
  hasToken,
} from "./cardComponents/utils";

// ─── Utils ────────────────────────────────────────────────────────────────────

const AutoAdvance = ({ onNext, delay }) => {
  useEffect(() => {
    const t = setTimeout(onNext, delay);
    return () => clearTimeout(t);
  }, [onNext, delay]);
  return null;
};

// ════════════════════════════════════════════════════════════════
// STEP 1 — Intro
// ════════════════════════════════════════════════════════════════
const Step1 = ({ onNext, onBack, isDesktop }) => {
  const { width, height } = useWindowSize(); // ← reactivo

  const isMedium = isDesktop && width < 1600;
  const heightContainer =
    height < 700 ? "46dvh" : height <= 840 ? "65dvh" : "71dvh";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: isDesktop ? "-webkit-fill-available" : heightContainer,
        width: "100%",
        padding: isDesktop ? "0 clamp(20px, 4vw, 80px)" : "0",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: isDesktop ? (isMedium ? "40px" : "80px") : "25px",
          flex: 1,
        }}
      >
        {/* Título */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: isDesktop ? "clamp(28px, 4vw, 40px)" : "24px",
                fontWeight: 800,
                color: "#fff",
              }}
            >
              Conecta tu tarjeta débito o crédito
            </span>
            {/* Logos Visa + Mastercard */}
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <VisaLogo />

              <MastercardLogo size={32} />
            </div>
          </div>
          <p
            style={{
              color: "#9b93b8",
              fontSize: isDesktop ? "14px" : "13px",
              marginTop: "6px",
              lineHeight: "1.5",
            }}
          >
            Ingresa los datos de tu tarjeta para activar pagos automáticos
            seguros.
          </p>
        </div>

        {/* Resumen */}

        <div
          style={{
            background: "#565584",
            borderRadius: "14px",
            padding: isDesktop ? "20px 24px" : "14px 16px",
            display: "flex",
            flexDirection: "column",
            gap: isDesktop ? "14px" : "10px",
          }}
        >
          <span style={{ fontWeight: 700, color: "#E1DFE5", fontSize: "15px" }}>
            Resumen de conexión
          </span>
          {[
            "Ten a la mano tu tarjeta débito/crédito que deseas conectar.",
            "Paga tu crédito de forma más rápida y segura.",
            "Los datos de tu tarjeta serán procesados de forma encriptada.",
          ].map((text, i) => (
            <div
              key={i}
              style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}
            >
              {/* Check activo no manipulable */}
              <div
                style={{
                  minWidth: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  background: "rgb(41 33 79)",
                  flexShrink: 0,
                  marginTop: "2px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path
                    d="M2 5l2.5 2.5L8 3"
                    stroke="#fff"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span
                style={{
                  color: "white",
                  fontSize: isDesktop ? "14px" : "13px",
                  lineHeight: "1.4",
                }}
              >
                {text}
              </span>
            </div>
          ))}
        </div>

        {/* Banner seguridad */}
        <div
          className="bg-purple"
          style={{
            borderRadius: "12px",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div
            style={{
              flexShrink: 0,
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: "#2a2550",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect
                x="5"
                y="11"
                width="14"
                height="10"
                rx="2"
                stroke="white"
                strokeWidth="1.8"
              />
              <path
                d="M8 11V7a4 4 0 1 1 8 0v4"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <circle cx="12" cy="16" r="1.5" fill="white" />
            </svg>
          </div>
          <p style={{ fontSize: "14px", margin: 0 }}>
            <span style={{ color: "white" }}>Tus credenciales </span>
            <span style={{ color: "white", fontWeight: 700 }}>
              nunca pasan{" "}
            </span>
            <span style={{ color: "white" }}>
              por nosotros. Todo es directamente con tu entidad bancaria.
            </span>
          </p>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: isDesktop ? "flex-end" : "stretch",
          marginTop: "auto", // ← empuja los botones al fondo igual que space-between
        }}
      >
        <div
          style={{
            width: isDesktop ? "400px" : "-webkit-fill-available",
            display: "flex",
          }}
        >
          <PrevButton onBack={onBack} label="Atrás" isMobile={!isDesktop} />
          <NextButton onNext={onNext} label="Continuar" isMobile={!isDesktop} />
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// STEP 2 — Formulario con tarjeta animada
// ════════════════════════════════════════════════════════════════
// ─────────────────────────────────────────────────────────────────────────────
// UTILIDADES DE VALIDACIÓN (sin dependencias externas)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Algoritmo de Luhn — verifica que el número de tarjeta sea matemáticamente válido.
 * @param {string} cardNumber  Número con o sin espacios
 * @returns {boolean}
 */
const luhnCheck = (cardNumber) => {
  const digits = cardNumber.replace(/\D/g, "");
  if (digits.length < 13) return false;

  let sum = 0;
  let isEven = false;

  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits[i], 10);
    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    isEven = !isEven;
  }
  return sum % 10 === 0;
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────────────────────

const Step2 = ({
  onNext,
  onBack,
  isDesktop,
  cardData,
  setCardData,
  onTokenize,
  creditUserStore,
}) => {
  const { width, height } = useWindowSize(); // ← reactivo

  const isMedium = isDesktop && width < 1200;
  const isSmallLaptop = isDesktop && height < 800;
  const number_document = localStorage.getItem("doc");
  const customer_email = localStorage.getItem("email");

  const heightContainer =
    height < 700 ? "46dvh" : height <= 840 ? "65dvh" : "71dvh";

  const [isFlipped, setIsFlipped] = useState(false);
  const [errors, setErrors] = useState({});

  const handleNext = async () => {
    if (!validate()) return;

    // Arma el JSON para Wompi
    const raw = cardData.cardNumber.replace(/\s/g, "");
    const [exp_month, exp_year] = cardData.expiry.split("/");

    const payload = {
      number: raw,
      exp_month,
      exp_year,
      cvc: cardData.cvc,
      card_holder: `${creditUserStore.firstname} ${creditUserStore.firstlastname}`,
      customer_email,
      number_document,
    };

    onTokenize(payload); // ← salta a Step3 y ejecuta la petición
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCardNumber = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setCardData((prev) => ({ ...prev, cardNumber: formatted }));
    if (errors.cardNumber) setErrors((p) => ({ ...p, cardNumber: "" }));
  };

  const handleExpiry = (e) => {
    const formatted = formatExpiry(e.target.value);
    setCardData((prev) => ({ ...prev, expiry: formatted }));
    if (errors.expiry) setErrors((p) => ({ ...p, expiry: "" }));
  };

  const handleCvc = (e) => {
    // La longitud máxima del CVC depende de la red (Amex = 4, resto = 3)
    const network = detectCardNetwork(cardData.cardNumber);
    const val = e.target.value.replace(/\D/g, "").slice(0, network.cvvLen);
    setCardData((prev) => ({ ...prev, cvc: val }));
    if (errors.cvc) setErrors((p) => ({ ...p, cvc: "" }));
  };

  // ── Validaciones ──────────────────────────────────────────────────────────

  const validate = () => {
    const errs = {};
    const rawNumber = cardData.cardNumber.replace(/\s/g, "");
    const network = detectCardNetwork(rawNumber);

    // 1. Longitud esperada según la red detectada
    if (!network.lengths.includes(rawNumber.length)) {
      errs.cardNumber = `Ingresa los ${network.lengths.join(" o ")} dígitos de tu tarjeta`;
    }
    // 2. Algoritmo de Luhn (solo si la longitud ya es correcta)
    else if (!luhnCheck(rawNumber)) {
      errs.cardNumber = "El número de tarjeta no es válido";
    }

    // 3. Fecha de expiración
    if (!cardData.expiry || cardData.expiry.length < 5) {
      errs.expiry = "Fecha inválida (MM/AA)";
    } else {
      const [monthStr, yearStr] = cardData.expiry.split("/");
      const month = parseInt(monthStr, 10);
      const year = parseInt(`20${yearStr}`, 10);

      const now = new Date();
      const currentMonth = now.getMonth() + 1; // 1-12
      const currentYear = now.getFullYear(); // ej: 2026

      if (month < 1 || month > 12) {
        errs.expiry = "El mes debe estar entre 01 y 12";
      } else if (year < currentYear) {
        errs.expiry = "La tarjeta está vencida";
      } else if (year === currentYear && month < currentMonth) {
        errs.expiry = "La tarjeta está vencida";
      }
    }

    // 4. CVC — longitud dinámica según la red (Amex = 4, resto = 3)
    if (!cardData.cvc || cardData.cvc.length < network.cvvLen) {
      errs.cvc = `CVC inválido (debe tener ${network.cvvLen} dígitos)`;
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Render (sin cambios) ───────────────────────────────────────────────────

  return (
    <Fragment>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: isDesktop ? "-webkit-fill-available" : heightContainer,
          padding: isDesktop ? "0 clamp(20px, 4vw, 80px)" : "0",
          width: "100%",
          gap: isSmallLaptop ? "8px" : isMedium ? "12px" : "16px",
        }}
      >
        {/* Título — solo desktop */}
        {isDesktop && (
          <div>
            <h2
              style={{
                fontSize: "clamp(18px, 2.5vw, 36px)",
                fontWeight: 800,
                color: "#fff",
                marginBottom: "2px",
              }}
            >
              Ingresa el número de tu tarjeta
            </h2>
            <p
              style={{
                color: "#9b93b8",
                fontSize: "clamp(11px, 1vw, 14px)",
                margin: 0,
              }}
            >
              Digita los 16 dígitos de tu tarjeta de crédito o débito.
            </p>
          </div>
        )}

        {/* Tarjeta — escala según pantalla */}
        <div
          style={{
            display: "flex",
            flexDirection: isSmallLaptop ? "row-reverse" : "column",
            justifyContent: isSmallLaptop && "center",
            alignItems: isSmallLaptop && "center",
            gap: isDesktop ? "0px" : "50px",
          }}
        >
          <div
            style={{
              transform: isMedium ? "scale(0.85)" : "scale(1)",
              transformOrigin: "top center",
              marginBottom: isMedium ? "-28px" : "0",
              transition: "transform 0.2s",
            }}
          >
            <AnimatedCard
              cardNumber={cardData.cardNumber}
              expiry={cardData.expiry}
              cvc={cardData.cvc}
              isFlipped={isFlipped}
              holderName={
                creditUserStore.firstname + " " + creditUserStore.firstlastname
              }
            />
          </div>

          {/* Campos */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: isMedium ? "8px" : "12px",
              flex: 1,
            }}
          >
            <StepInput
              name="cardNumber"
              value={cardData.cardNumber}
              label="Número de la tarjeta"
              onChange={handleCardNumber}
              error={errors.cardNumber}
              placeholder="XXXX XXXX XXXX XXXX"
              type="tel"
              inputMode="numeric"
              required
              autoFocus
              isDesktop={isDesktop}
              maxLength={19}
              onFocus={() => setIsFlipped(false)}
            />

            <div style={{ display: "flex", gap: "12px" }}>
              <div style={{ flex: 1 }}>
                <StepInput
                  name="expiry"
                  value={cardData.expiry}
                  label="Fecha de expiración"
                  onChange={handleExpiry}
                  error={errors.expiry}
                  placeholder="MM/AA"
                  type="tel"
                  inputMode="numeric"
                  required
                  isDesktop={isDesktop}
                  onFocus={() => setIsFlipped(false)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <StepInput
                  name="cvc"
                  value={cardData.cvc}
                  label="CVC"
                  onChange={handleCvc}
                  error={errors.cvc}
                  placeholder="•••"
                  type="tel"
                  inputMode="numeric"
                  required
                  isDesktop={isDesktop}
                  onFocus={() => setIsFlipped(true)}
                  onBlur={() => setIsFlipped(false)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Botones */}
        <div
          style={{
            display: "flex",
            justifyContent: isDesktop ? "flex-end" : "stretch",
          }}
        >
          <div
            style={{
              width: isDesktop ? "400px" : "-webkit-fill-available",
              display: "flex",
            }}
          >
            <PrevButton onBack={onBack} label="Atrás" isMobile={!isDesktop} />
            <NextButton
              onNext={handleNext}
              label="Continuar"
              isMobile={!isDesktop}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};
// ════════════════════════════════════════════════════════════════
// STEP 3 — Procesando con Wompi
// ════════════════════════════════════════════════════════════════

const Step3 = ({ onNext, isDesktop }) => {
  const { width } = useWindowSize();

  const isMedium = isDesktop && width < 1200;

  return (
    <div
      className="flex flex-col items-center w-full"
      style={{ paddingTop: "20px", gap: isDesktop ? "24px" : "16px" }}
    >
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-10px); opacity: 1; }
        }
      `}</style>

      <h2
        style={{
          color: "#fff",
          fontWeight: 800,
          fontSize: isDesktop ? "clamp(30px, 4vw, 48px)" : "30px",
          alignSelf: isDesktop ? "flex-start" : "center",
        }}
      >
        Conectando tu tarjeta
      </h2>

      {/* Layout: desktop = fila | mobile = columna centrada */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: isDesktop ? "center" : "center",
          gap: isDesktop ? "32px" : "35px",
          width: !isDesktop && "100%",
        }}
      >
        {/* Logos + dots — siempre juntos */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: !isDesktop && "flex-end",
            gap: isDesktop ? "16px" : "10px",
            width: !isDesktop && "42%",
          }}
        >
          <GalileIcon
            style={{
              width: isDesktop ? "100px" : "80px",
              height: isDesktop ? "100px" : "80px",
            }}
            alt="Galilea"
          />
        </div>
        <div
          style={{
            display: "flex",
            gap: isDesktop ? "8px" : "6px",
            alignItems: "center",
            width: !isDesktop && "17%",
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                width: isDesktop ? "14px" : "12px",
                height: isDesktop ? "14px" : "12px",
                borderRadius: "50%",
                backgroundColor: "white",
                animation: `bounce 1.2s ease-in-out ${i * 0.15}s infinite`,
              }}
            />
          ))}
        </div>

        {/* Tarjeta — centrada y escalada correctamente */}
        <div
          style={{
            transform: isDesktop
              ? isMedium
                ? "scale(0.6)"
                : "scale(0.7)"
              : "scale(0.5)",
            transformOrigin: "center left",
            // marginBottom compensa el espacio del scale
            marginBottom: isDesktop && (isMedium ? "-20px" : "-10px"),
            width: !isDesktop && "42%",
          }}
        >
          <AnimatedCard cardNumber={""} expiry={""} cvc={""} />
        </div>
      </div>

      {/* Card checks */}
      <div
        style={{
          background: "#565584",
          borderRadius: "14px",
          padding: isDesktop ? "20px 24px" : "14px 16px",
          display: "flex",
          flexDirection: "column",
          gap: isDesktop ? "14px" : "10px",
          width: isDesktop ? (isMedium ? "100%" : "800px") : "100%",
        }}
      >
        {[
          "Tus datos viajan cifrados.",
          "El número de tarjeta nunca se almacena.",
        ].map((text, i) => (
          <div
            key={i}
            style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}
          >
            <div
              style={{
                minWidth: "18px",
                height: "18px",
                borderRadius: "50%",
                background: "rgb(41 33 79)",
                flexShrink: 0,
                marginTop: "2px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path
                  d="M2 5l2.5 2.5L8 3"
                  stroke="#fff"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span
              style={{
                color: "white",
                fontSize: isDesktop ? "14px" : "13px",
                lineHeight: "1.4",
              }}
            >
              {text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// STEP 4 — Éxito
// ════════════════════════════════════════════════════════════════
const Step4 = ({ isDesktop, onComplete }) => {
  const { height } = useWindowSize();

  let heightCanvan = height < 750 ? "17rem" : height > 900 ? "25rem" : "20rem";
  let widthCanvan = height < 750 ? "15rem" : height > 900 ? "23rem" : "20rem";

  let heightContainer =
    height < 750 ? "19rem" : height > 900 ? "30rem" : "20rem";

  return (
    <div className="flex flex-col items-center justify-center">
      {/* Componente de animación Rive */}
      <h1
        className="font-bold2"
        style={{
          fontSize: "2rem",
          width: "300px",
          textAlign: "center",
        }}
      >
        ¡Luz Púrpura para Continuar!
      </h1>
      <AnimationComponent
        animationUrl="/animations/checkanimation.riv"
        width={heightCanvan}
        height={widthCanvan}
        heightContainer={heightContainer}
        isDesktop={isDesktop}
        stateMachines={["chek-animation"]}
      />
      <p
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "200px",
        }}
      >
        <span>
          ¡<span style={{ color: "#A68AFF" }}>Felicitaciones</span>!
        </span>
        <span
          style={{
            textAlign: "center",
          }}
        >
          Conexión segura realizada.
        </span>
      </p>
      <p
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <span
          style={{
            textAlign: "center",
          }}
        >
          Tu tarjeta fue conectada de forma segura.
        </span>
      </p>
      <AutoAdvance onNext={onComplete} delay={2500} />
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// ORQUESTADOR
// ════════════════════════════════════════════════════════════════
const CardFlow = ({ bankValue, isDesktop, onComplete, onBack }) => {
  const [step, setStep] = useState(1);
  const [tokenError, setTokenError] = useState(null);
  const [, setTokenIdWompi] = useState("");

  const [cardData, setCardData] = useState({
    cardNumber: "",
    expiry: "",
    cvc: "",
  });
  const { stepCredit, saveSteps, loadSteps } = stepsCreditStore();
  const { creditUserStore, loadUser } = useCreditStore();
  const { showAlert } = useAlert();

  useEffect(() => {
    loadSteps();
    loadUser();
  }, [loadSteps, loadUser]);

  useEffect(() => {
    if (!stepCredit?.stepFlow) return;
    let tokenIdWompi = localStorage.getItem("tokenIdWompi");
    if (hasToken(tokenIdWompi)) {
      setStep(stepCredit.stepFlow);
    }
  }, [stepCredit]);

  const next = () => {
    const nextStep = step + 1;
    setStep(nextStep);
    saveSteps({
      ...stepCredit,
      stepFlow: nextStep,
    });
  };

  const prev = () => {
    if (step === 1) {
      onBack();
    } else {
      const prevStep = step - 1;
      setStep(prevStep);
      saveSteps({
        ...stepCredit,
        stepFlow: prevStep,
      });
    }
  };

  const handleTokenize = async (payloadData) => {
    setTokenError(null);

    // ← Explícito, no depende del closure de next()
    setStep(3);
    saveSteps({ ...stepCredit, stepFlow: 3 });

    let payload = {
      bankName: "card",
      dataTokenization: payloadData,
    };

    try {
      const tokenSession = localStorage.getItem("token_jwt");

      const res = await axios.post(
        `${variablesGlobales.urlServiceBackend}/api/v1/tokenization/createTokenization`,
        payload,
        {
          headers: {
            authorization: `Bearer ${tokenSession}`,
          },
        },
      );

      const data = res.data;

      if (data?.status === "AVAILABLE") {
        showAlert("info", normalizeMessage(data?.message), 6000);
        setTokenIdWompi(data?.card_token_data?.id);
        localStorage.setItem("tokenIdWompi", data?.card_token_data?.id);
        setStep(4);
        saveSteps({ ...stepCredit, stepFlow: 4 });
      } else {
        showAlert(
          "info",
          data?.message || "Error al conectar la tarjeta",
          8000,
        );
        setTimeout(() => {
          setStep(2);
          saveSteps({ ...stepCredit, stepFlow: 2 });
        }, 5000);
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        "Error de conexión. Intenta nuevamente.";
      console.error("Wompi error:", err?.response?.data);
      showAlert("info", errorMsg, 8000);
      setTokenError("Error de conexión. Intenta nuevamente.");
      setTimeout(() => {
        setStep(2);
        saveSteps({ ...stepCredit, stepFlow: 2 });
      }, 5000);
    }
  };

  switch (step) {
    case 1:
      return (
        <Step1
          onNext={next}
          onBack={prev}
          isDesktop={isDesktop}
          bankValue={bankValue}
        />
      );
    case 2:
      return (
        <Step2
          onNext={next}
          onBack={prev}
          isDesktop={isDesktop}
          cardData={cardData}
          setCardData={setCardData}
          bankValue={bankValue}
          onTokenize={handleTokenize} // ← nuevo prop
          tokenError={tokenError}
          creditUserStore={creditUserStore}
        />
      );
    case 3:
      return (
        <Step3
          onNext={next}
          isDesktop={isDesktop}
          cardData={cardData}
          bankValue={bankValue}
        />
      );
    case 4:
      return (
        <Step4
          onComplete={onComplete}
          isDesktop={isDesktop}
          bankValue={bankValue}
        />
      );
    default:
      return null;
  }
};

// const CardFlow = (props) => (
//     <CardFlowInner {...props} />
// );

export default CardFlow;
